import { useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Switch,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EditOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  pushCampaignsApi,
  type PushCampaign,
  type UpdatePushCampaign,
} from '@/api/push-campaigns.api';

const { Title, Text } = Typography;

// cron(UTC) → 사람이 읽는 KST 문구. ';' 로 여러 패턴(요일별 시각) 지원.
function describeCron(expr: string): string {
  return expr
    .split(';')
    .map((p) => describeCronOne(p.trim()))
    .filter(Boolean)
    .join(' / ');
}
function describeCronOne(p: string): string {
  const parts = p.split(/\s+/);
  if (parts.length < 5) return p;
  const [min, hour, , , dow] = parts;
  const h = Number(hour);
  const m = Number(min);
  if (Number.isNaN(h) || Number.isNaN(m)) return p;
  const kstH = (h + 9) % 24; // UTC→KST (낮시간 캠페인이라 자정 넘김 요일시프트는 미고려)
  const time = `${String(kstH).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  const label = describeDow(dow);
  return label ? `${label} ${time}` : time;
}
function describeDow(dow: string): string {
  if (!dow || dow === '*') return '매일';
  if (dow === '1-5') return '평일';
  if (dow === '0,6' || dow === '6,0') return '주말';
  const names = ['일', '월', '화', '수', '목', '금', '토'];
  return dow.split(',').map((d) => names[Number(d)] ?? d).join('·');
}

export function PushCampaignsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['push-campaigns'],
    queryFn: pushCampaignsApi.list,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdatePushCampaign }) =>
      pushCampaignsApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['push-campaigns'] });
      message.success('저장되었습니다.');
    },
    onError: () => message.error('저장에 실패했습니다.'),
  });

  const [editing, setEditing] = useState<PushCampaign | null>(null);
  const [form] = Form.useForm();
  const cronWatch = Form.useWatch('cronExpr', form);

  const openEdit = (c: PushCampaign) => {
    setEditing(c);
    form.setFieldsValue({
      name: c.name,
      deepLink: c.deepLink,
      cronExpr: c.cronExpr,
      rotation: c.rotation,
      description: c.description,
      messages: c.messages.length > 0 ? c.messages : [{ title: '', body: '' }],
    });
  };

  const handleSave = async () => {
    if (!editing) return;
    const vals = await form.validateFields();
    await updateMutation.mutateAsync({ id: editing.id, body: vals });
    setEditing(null);
  };

  const columns: ColumnsType<PushCampaign> = [
    {
      title: '키',
      dataIndex: 'key',
      width: 150,
      render: (k: string) => <Text code>{k}</Text>,
    },
    { title: '이름', dataIndex: 'name', width: 160 },
    {
      title: 'ON',
      dataIndex: 'enabled',
      width: 64,
      render: (v: boolean, r) => (
        <Switch
          checked={v}
          loading={updateMutation.isPending}
          onChange={(checked) =>
            updateMutation.mutate({ id: r.id, body: { enabled: checked } })
          }
        />
      ),
    },
    {
      title: '발송',
      dataIndex: 'triggerType',
      width: 220,
      render: (t: string, r) => (
        <Space direction="vertical" size={0}>
          <Tag>{t}</Tag>
          {r.cronExpr && (
            <>
              <Text style={{ fontSize: 12 }}>
                {describeCron(r.cronExpr)} <Text type="secondary" style={{ fontSize: 10 }}>KST</Text>
              </Text>
              <Text type="secondary" style={{ fontSize: 10, fontFamily: 'monospace' }}>
                {r.cronExpr}
              </Text>
            </>
          )}
        </Space>
      ),
    },
    {
      title: '대상',
      dataIndex: 'targetType',
      width: 110,
      render: (t: string) => (
        <Tag color={t === 'ALL_ACTIVE' ? 'blue' : 'default'}>{t}</Tag>
      ),
    },
    {
      title: '문구',
      dataIndex: 'messages',
      width: 60,
      render: (m: PushCampaign['messages']) => `${m.length}개`,
    },
    {
      title: '딥링크',
      dataIndex: 'deepLink',
      render: (v: string | null) =>
        v ? (
          <Text code style={{ fontSize: 11 }}>{v}</Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: '',
      width: 56,
      render: (_, r) => (
        <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(r)} />
      ),
    },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 4 }}>
        푸시 캠페인
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
        푸시 문구·ON/OFF·딥링크·발송 시각을 DB에서 관리합니다. (문구·ON/OFF·딥링크는 즉시 반영, 발송 시각은 서버 재시작 후 반영 / 타겟 조건은 코드)
      </Text>

      <Card style={{ borderRadius: 8 }}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data ?? []}
          loading={isLoading}
          pagination={false}
          scroll={{ x: 1000 }}
        />
      </Card>

      <Modal
        open={!!editing}
        title={`캠페인 수정 — ${editing?.key ?? ''}`}
        onCancel={() => setEditing(null)}
        onOk={handleSave}
        confirmLoading={updateMutation.isPending}
        okText="저장"
        cancelText="취소"
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="name" label="이름" rules={[{ required: true, message: '이름을 입력하세요' }]}>
            <Input />
          </Form.Item>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="deepLink" label="딥링크" style={{ flex: 1 }}>
              <Input placeholder="/home" />
            </Form.Item>
            <Form.Item name="rotation" label="문구 선택 방식" style={{ flex: 1 }}>
              <Select
                options={[
                  { value: 'DAILY_INDEX', label: 'DAILY_INDEX (날짜순환)' },
                  { value: 'RANDOM_NODUP', label: 'RANDOM_NODUP (직전제외 랜덤)' },
                  { value: 'SINGLE', label: 'SINGLE (첫 문구)' },
                ]}
              />
            </Form.Item>
            <Form.Item name="cronExpr" label="발송 시각 (cron, UTC)" style={{ flex: 1 }}>
              <Input placeholder="55 8 * * 1-5;30 7 * * 0,6" />
            </Form.Item>
          </Space>
          {cronWatch && (
            <Text
              type="secondary"
              style={{ display: 'block', marginTop: -12, marginBottom: 12, fontSize: 12 }}
            >
              → 발송 시각(KST): <Text strong style={{ fontSize: 12 }}>{describeCron(cronWatch)}</Text>
              {'  '}· 시각 변경은 서버 재시작 후 반영 · {';'} 로 여러 시각(요일별) 가능
            </Text>
          )}
          <Form.Item name="description" label="메모">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Text strong>문구 목록</Text>
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
            ({'{'}sportType{'}'}, {'{'}drop{'}'} 등 변수는 발송 시 자동 치환)
          </Text>
          <Form.List name="messages">
            {(fields, { add, remove }) => (
              <div style={{ marginTop: 8 }}>
                {fields.map((field) => (
                  <Space key={field.key} align="start" style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item
                      name={[field.name, 'title']}
                      rules={[{ required: true, message: '제목' }]}
                      style={{ marginBottom: 0, width: 200 }}
                    >
                      <Input placeholder="제목" />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, 'body']}
                      rules={[{ required: true, message: '본문' }]}
                      style={{ marginBottom: 0, flex: 1, minWidth: 340 }}
                    >
                      <Input placeholder="본문" />
                    </Form.Item>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => remove(field.name)}
                    />
                  </Space>
                ))}
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => add({ title: '', body: '' })}
                  block
                >
                  문구 추가
                </Button>
              </div>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
}
